const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (value) => EMAIL_PATTERN.test((value || "").trim());

// Roles that are inherently cross-org/global — an org_id doesn't apply to
// them, so the org picker is skipped and org_id is omitted from the payload.
const ORG_EXEMPT_ROLE_NAMES = ["EnterpriseAdmin", "OperationsAdmin"];

export const isOrgExemptRole = (roleName) => ORG_EXEMPT_ROLE_NAMES.includes(roleName);

// "User" is the default role every account gets automatically — it is never
// something an admin picks to assign, so it must not appear as a selectable
// option in any assign/edit role picker (org-scoped or platform-wide).
export const isDefaultUserRole = (roleName) => roleName === "User";

// Trimmed, non-empty, de-duplicated org ids (order kept).
const cleanOrgIds = (ids) => [...new Set((ids || []).map((id) => (typeof id === "string" ? id.trim() : "")).filter(Boolean))];

export const buildEmptyAssignmentForm = (defaults = {}) => ({
  targetType: "user", // "user" | "application"
  roleId: defaults.roleId || "",
  userId: "",
  email: defaults.email || "",
  appId: "",
  // POST /role-assignments takes org_id as a LIST, so one assignment can cover
  // several organizations at once. (PUT still takes a single org_id string.)
  orgIds: [],
  assignedBy: defaults.assignedBy || "",
  isActive: true,
});

// Validates the create-assignment form. Returns a field -> message map; empty
// object means the form is valid. Mirrors the API's own required-field rules
// so client-side validation and server validation stay in sync.
export const validateAssignmentForm = (form, { selectedRoleName } = {}) => {
  const errors = {};

  if (!form.roleId) errors.roleId = "Role is required.";

  if (form.targetType === "user") {
    if (!form.userId?.trim() && !form.email?.trim()) {
      errors.userId = "Provide a user ID or an email.";
      errors.email = "Provide a user ID or an email.";
    } else if (form.email?.trim() && !isValidEmail(form.email)) {
      errors.email = "Enter a valid email address.";
    }
  }

  // At least one org is required for every role except the org-exempt
  // (cross-org) roles, regardless of target type.
  if (!isOrgExemptRole(selectedRoleName) && cleanOrgIds(form.orgIds).length === 0) {
    errors.orgIds = "Select at least one organization for this role.";
  }

  return errors;
};

// Builds the POST /role-assignments request body from validated form state.
// Orgs this user already holds this role in (active, user assignments only),
// so the Assign role drawer can show them as taken. Matches on email
// (case-insensitive) or user id; org_id may be a string or a list.
export const getAlreadyAssignedOrgIds = (assignments, { roleId, email, userId } = {}) => {
  const mail = email?.trim().toLowerCase();
  const uid = userId?.trim();
  if (!roleId || (!mail && !uid)) return [];
  const ids = new Set();
  (assignments || []).forEach((a) => {
    if (a.is_app || a.is_active === false || a.role_id !== roleId) return;
    const sameUser = (mail && a.email?.toLowerCase() === mail) || (uid && a.user_id === uid);
    if (sameUser) cleanOrgIds(Array.isArray(a.org_id) ? a.org_id : [a.org_id]).forEach((id) => ids.add(id));
  });
  return [...ids];
};

export const buildCreateAssignmentPayload = (form, { selectedRoleName, platformId } = {}) => {
  const isApp = form.targetType === "application";

  const payload = {
    role_id: form.roleId,
    is_app: isApp,
    assigned_by: form.assignedBy.trim(),
    is_active: form.isActive,
    platform_id: platformId || undefined,
  };

  if (isApp) {
    payload.app_id = form.appId?.trim() || undefined;
  } else {
    if (form.userId?.trim()) payload.user_id = form.userId.trim();
    if (form.email?.trim()) payload.email = form.email.trim();
  }

  const orgIds = cleanOrgIds(form.orgIds);
  if (!isOrgExemptRole(selectedRoleName) && orgIds.length > 0) {
    payload.org_id = orgIds;
  }

  return payload;
};

// Builds the PUT /role-assignments request body (an array of patches) for
// editing a single existing assignment. Only API-editable fields are included.
export const buildUpdateAssignmentPayload = ({ id, roleId, email, orgId, assignedBy, isActive, platformId }) => [
  {
    id,
    role_id: roleId,
    email: email?.trim() || undefined,
    org_id: orgId?.trim() || undefined,
    platform_id: platformId || undefined,
    assigned_by: assignedBy?.trim(),
    is_active: isActive,
  },
];

// PUT patches that only flip is_active (deactivate / reactivate).
export const buildSetActivePayload = (assignmentIds, isActive, platformId) =>
  assignmentIds.map((id) => ({ id, is_active: isActive, platform_id: platformId || undefined }));

// Role hierarchy: you can only assign/edit/activate/deactivate/delete an
// assignment whose role is at or below your own highest-ranked role. Ranked
// by each role's own `priority` number (already maintained by the backend
// and used elsewhere for the same purpose — see RolesPage/RoleEditDrawer's
// "top priority" concept) rather than hardcoding role names, so this works
// for any current or future role without needing to know its name.
//
// LOWER priority number = MORE senior (e.g. EnterpriseAdmin=10 outranks
// OrganizationOwner=50, which outranks User=90) — confirmed against the real
// GET /roles data. So "my highest-ranked role" is the one with the LOWEST
// number (Math.min), and a role with no priority set is treated as
// Number.MAX_SAFE_INTEGER (the least senior rank) rather than 0, since 0
// would otherwise look more senior than every real role.
export const getMyMaxRolePriority = (myRoleNames, allRoles) => {
  const rolesByName = new Map((allRoles || []).map((role) => [role.role_name, role]));
  return (myRoleNames || []).reduce((min, name) => {
    const priority = rolesByName.get(name)?.priority ?? Number.MAX_SAFE_INTEGER;
    return Math.min(min, priority);
  }, Number.MAX_SAFE_INTEGER);
};

// A role with no priority set (undefined/null) is treated as the least
// senior rank (Number.MAX_SAFE_INTEGER) rather than 0, so it's only
// manageable by someone whose own priority is also unranked — never
// automatically manageable by everyone.
export const canManageRole = (targetRole, myMaxPriority) =>
  (targetRole?.priority ?? Number.MAX_SAFE_INTEGER) >= myMaxPriority;
